import { useMemo } from 'react';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import { useAppStore } from '../state/store';
import { useBIMStore } from '../state/useBIMStore';
import { STATUS_COLORS } from '../themes/colors';

/**
 * Crop Region overlay (BIM-style)
 *
 * When the active view has a crop region, draws its rectangle boundary in
 * plan/3D. "Crop View" controls whether the crop actually clips the model
 * (consumed in BimViewport via renderer clipping planes); "Show Crop
 * Region" controls the annotation rectangle visibility, exactly like the
 * BIM control bar pair.
 */

const DEFAULT_EXTENT = 25;
const CROP_HEIGHT = 12;

export function CropRegionOverlay() {
    const showCropRegion = useAppStore((s) => s.view.showCropRegion);
    const cropView = useAppStore((s) => s.view.cropView);
    const activeViewId = useBIMStore((s) => s.project.activeViewId);
    const cropRegion = useBIMStore((s) => s.project.views.get(s.project.activeViewId)?.cropRegion);

    const outline = useMemo(() => {
        const x = cropRegion?.x ?? -DEFAULT_EXTENT;
        const y = cropRegion?.y ?? -DEFAULT_EXTENT;
        const w = cropRegion?.width ?? DEFAULT_EXTENT * 2;
        const h = cropRegion?.height ?? DEFAULT_EXTENT * 2;
        return [
            new THREE.Vector3(x, y, 0),
            new THREE.Vector3(x + w, y, 0),
            new THREE.Vector3(x + w, y + h, 0),
            new THREE.Vector3(x, y + h, 0),
            new THREE.Vector3(x, y, 0),
        ];
    }, [cropRegion]);

    if (!showCropRegion || activeViewId === undefined) return null;

    const color = cropView ? STATUS_COLORS.warning : STATUS_COLORS.info;

    return (
        <group>
            {/* Base rectangle on the ground plane */}
            <Line points={outline} color={color} lineWidth={1.5} />

            {/* Vertical corner posts (3D cue, clipped to nothing in plan view) */}
            {[0, 1, 2, 3].map((i) => {
                const p = outline[i];
                return (
                    <Line
                        key={i}
                        points={[p, new THREE.Vector3(p.x, p.y, CROP_HEIGHT)]}
                        color={color}
                        lineWidth={1}
                        dashed
                        dashSize={0.4}
                        gapSize={0.2}
                    />
                );
            })}
        </group>
    );
}
